import { Scale, ShieldAlert, HeartPulse, Wrench, MapPin, ShieldPlus, Sparkles, Handshake, BadgeCheck, Users, MessagesSquare, ClipboardList } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  useSeoMeta, LandingHero, CoverageGrid, WhyUsSection,
  TestimonialsSection, FaqSection, Breadcrumb,
} from "../lib/landingShared";

const HERO_IMG = "https://images.pexels.com/photos/8769465/pexels-photo-8769465.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700";

const COVERAGE = [
  { icon: Scale,       title: "Responsabilità civile verso terzi", desc: "Danni involontari causati a persone, veicoli o cose durante la circolazione con il monopattino." },
  { icon: ShieldAlert, title: "Infortuni del conducente",           desc: "Indennizzo in caso di infortuni riportati alla guida, con capitale per invalidità permanente." },
  { icon: HeartPulse,  title: "Assistenza medica e ricovero",        desc: "Rimborso spese sanitarie e indennità da ricovero conseguenti a un sinistro." },
  { icon: Wrench,      title: "Danni al mezzo",                       desc: "Copertura opzionale per danni accidentali, incendio e atti vandalici al monopattino." },
  { icon: ShieldPlus,  title: "Furto e rapina",                       desc: "Indennizzo in caso di furto o rapina del monopattino, con soluzioni modulari." },
  { icon: MapPin,      title: "Tutela legale e assistenza",           desc: "Spese di difesa in ambito civile e amministrativo, assistenza stradale in caso di guasto." },
];

const WHY = [
  { icon: Handshake,      title: "Un colloquio, non un preventivo automatico", desc: "Uso urbano, extra-urbano, mezzo proprio o condiviso: la copertura nasce dal tuo profilo reale." },
  { icon: Sparkles,       title: "Garanzie modulabili",                         desc: "Solo RC obbligatoria o pacchetto completo con infortuni, furto e assistenza: scegliamo insieme." },
  { icon: BadgeCheck,     title: "Chiarezza sulle clausole",                    desc: "Massimali, franchigie, esclusioni e casi di rivalsa: te lo spieghiamo prima della firma." },
  { icon: Users,          title: "Più compagnie a confronto",                   desc: "Confrontiamo le condizioni delle principali compagnie del settore e proponiamo la più equilibrata." },
];

const FAQS = [
  { q: "L'assicurazione per il monopattino è obbligatoria?",
    a: "Sì. Dal 2024, con l'entrata in vigore del nuovo Codice della Strada, la Responsabilità Civile verso terzi (RC) è obbligatoria per la circolazione dei monopattini elettrici su strada pubblica. La polizza deve coprire i danni causati a persone o cose durante l'utilizzo del mezzo. È inoltre obbligatorio circolare con casco e, in alcune situazioni, targa e assicurazione visibile." },
  { q: "Chi deve assicurare il monopattino?",
    a: "L'obbligo assicurativo ricade sul proprietario del mezzo. È possibile stipulare polizze individuali per un singolo monopattino oppure estendere la copertura all'interno di una polizza famiglia esistente (in alcuni casi con specifiche appendici). Per i monopattini in sharing la copertura è normalmente inclusa nel servizio dell'operatore." },
  { q: "Quali danni copre la polizza?",
    a: "La copertura base (RC) risarcisce i danni a terzi causati durante la circolazione: lesioni fisiche a persone, danni ad altri veicoli, danni a cose. Le garanzie accessorie possono includere infortuni del conducente, rimborso spese mediche, assistenza legale, danni al monopattino (incendio, atti vandalici), furto e rapina. La combinazione esatta dipende dalla polizza scelta." },
  { q: "Posso richiedere un preventivo personalizzato?",
    a: "Certamente. Ogni situazione è diversa: uso urbano quotidiano, uso saltuario, monopattino proprio o condiviso, esistenza di altre polizze in famiglia. In consulenza analizziamo il tuo profilo e ti proponiamo la soluzione più equilibrata tra le compagnie con cui collaboriamo, con chiarezza su costi, massimali e clausole." },
  { q: "Quanto tempo serve per ricevere una proposta?",
    a: "In genere ti ricontattiamo entro 24 ore dalla richiesta di preventivo per fissare un breve colloquio (telefonico o in agenzia). Una volta raccolti i dati necessari, la proposta personalizzata arriva normalmente entro 1-3 giorni lavorativi, insieme al set informativo precontrattuale della compagnia scelta." },
];

const TRUST = [
  { value: "1958", label: "Anno di fondazione" },
  { value: "3",    label: "Generazioni di esperienza" },
  { value: "24h",  label: "Risposta alla richiesta" },
];

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQS.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
};

export default function Monopattino() {
  useSeoMeta({
    title: "Assicurazione Monopattino Elettrico — Polizza RC obbligatoria | Da Pra Assicurazioni",
    description: "Assicurazione monopattino elettrico su misura: RC obbligatoria, infortuni conducente, furto e assistenza. Consulenza dedicata, agenzia assicurativa plurimandataria dal 1958 a Milano.",
    keywords: "assicurazione monopattino, assicurazione monopattino elettrico, assicurazione monopattino obbligatoria, RC monopattino, polizza monopattino, preventivo assicurazione monopattino, assicurazione monopattino Milano",
    canonical: "https://dapraassicurazioni.it/assicurazione-monopattino",
    jsonLd: JSON_LD, jsonLdId: "ld-monopattino",
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <Breadcrumb current={{ label: "Assicurazione Monopattino", href: "/assicurazione-monopattino" }} />

      <LandingHero
        eyebrow="Monopattino Elettrico · Dal 1958"
        title={<>Assicurazione Monopattino Elettrico: <span className="italic">proteggi i tuoi spostamenti con la copertura più adatta.</span></>}
        description="Con l'introduzione dell'obbligo assicurativo per i monopattini elettrici, ti aiutiamo a individuare la soluzione più adatta alle tue esigenze grazie alla consulenza della nostra agenzia assicurativa plurimandataria."
        imageSrc={HERO_IMG}
        imageAlt="Assicurazione monopattino elettrico — Da Pra Assicurazioni"
        ctaPrimaryLabel="Richiedi un preventivo"
        ctaPrimaryHref="/preventivo?type=altro"
        trustItems={TRUST}
      />

      <CoverageGrid
        eyebrow="Cosa può coprire la polizza"
        heading="Cosa puoi proteggere."
        intro="Componi la copertura partendo dalla responsabilità civile obbligatoria, aggiungendo le garanzie che contano per il tuo utilizzo del mezzo."
        items={COVERAGE}
      />

      <WhyUsSection
        eyebrow="Perché sceglierci"
        heading="Consulenza plurimandataria, vera."
        body={[
          "Da tre generazioni accompagniamo persone e famiglie nella scelta delle coperture assicurative. Conosciamo le novità normative sulla mobilità elettrica e le condizioni offerte dalle principali compagnie.",
          "Non ti proponiamo un prodotto già confezionato: partiamo dal tuo utilizzo reale del monopattino — urbano, saltuario, quotidiano, condiviso — e costruiamo la copertura insieme, con chiarezza su cosa è incluso, cosa non lo è e cosa serve davvero.",
        ]}
        items={WHY}
      />

      <TestimonialsSection heading="La fiducia dei nostri clienti." />
      <FaqSection heading="Domande frequenti sull'assicurazione monopattino." faqs={FAQS} />

      <Footer />
    </div>
  );
}
